/**
 * InitializeTextEncryptionUseCase
 *
 * Pure-domain wrapper around `textEncryptionService.initialize`. Owns the
 * E2EE-init hop so feature hooks no longer reach into the encryption service
 * directly — they call `initializeTextEncryptionUseCase.execute(peerPublicJwk)`
 * once per peer (typically in the chat's initial-load phase) and the use case
 * propagates the call to the singleton service. Pair-of-three with
 * `EncryptTextUseCase` and `DecryptTextUseCase`.
 *
 * Flutter parity: there is no separate Flutter use-case layer for E2EE — the
 * presentation layer (`chat_screen.dart:283-298`) calls `_e2ee.initialize(...)`
 * directly. The split into three web use cases is a Clean-Architecture
 * boundary we add to keep the hook free of service ownership; the underlying
 * service call is unchanged.
 *
 * Service-singleton caveat: `textEncryptionService` is a singleton with a
 * single derived-key slot. Any caller that interleaves `initialize → encrypt`
 * for different peers MUST serialize the pair — see
 * `ShareWithFriendsUseCase`'s JSDoc for the full rationale (peer B's
 * `initialize` would clobber peer A's key before peer A's `encrypt` ran).
 * This use case does not own that serialization; the caller does.
 */

import {
  TextEncryptionService,
  textEncryptionService,
} from '@/domain/services/textEncryption/textEncryptionService';

export class InitializeTextEncryptionUseCase {
  private textEncryptionService: TextEncryptionService;

  constructor(service: TextEncryptionService = textEncryptionService) {
    this.textEncryptionService = service;
  }

  /**
   * Derive the AES-GCM key for the given peer. When `peerPublicJwk` is null
   * (or the underlying derivation fails) the service falls through to
   * pass-through mode — subsequent `encrypt`/`decrypt` calls return their
   * input unchanged (Flutter parity: `e2ee_impl.dart:47-50`).
   */
  async execute(peerPublicJwk: JsonWebKey | null): Promise<void> {
    return this.textEncryptionService.initialize(peerPublicJwk);
  }
}

export const initializeTextEncryptionUseCase = new InitializeTextEncryptionUseCase();
